import { useEffect, useState, type CSSProperties } from 'react';

// round3 #7 — 환경 설정 정리.
// 기존 톱니바퀴 팝오버에는 (1) 다크 모드 토글 (2) 정보 밀도 라디오가 있었으나,
// '정보 밀도'는 매칭되는 CSS([data-density])가 전혀 없어 실제로 아무 효과가 없는
// 무동작 컨트롤이었다 → 제거. 다크 모드만 남겨 톱바의 단독 해/달 아이콘 토글로 노출.
// (5원칙 ⑤: 이모지 금지 → inline SVG 아이콘 사용.)
//
// localStorage 지속 + DOM side-effect: dark → <html data-theme="dark">.

const STORAGE_KEY = 'kuun:tweaks';

interface Tweaks {
  dark: boolean;
}

const DEFAULT_TWEAKS: Tweaks = {
  dark: false,
};

function loadTweaks(): Tweaks {
  if (typeof window === 'undefined') return DEFAULT_TWEAKS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_TWEAKS;
    const parsed = JSON.parse(raw) as Partial<Tweaks>;
    return { dark: Boolean(parsed.dark ?? DEFAULT_TWEAKS.dark) };
  } catch {
    return DEFAULT_TWEAKS;
  }
}

function saveTweaks(t: Tweaks): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
  } catch {
    // localStorage quota / private mode — silently ignore.
  }
}

function applyTweaks(t: Tweaks): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (t.dark) {
    html.setAttribute('data-theme', 'dark');
  } else {
    html.removeAttribute('data-theme');
  }
}

/** Hook: subscribe to tweaks + apply DOM side-effects on mount. */
export function useTweaks(): [Tweaks, (next: Partial<Tweaks>) => void] {
  const [tweaks, setTweaksState] = useState<Tweaks>(() => loadTweaks());

  // Apply on every change.
  useEffect(() => {
    applyTweaks(tweaks);
  }, [tweaks]);

  function update(next: Partial<Tweaks>): void {
    setTweaksState((prev) => {
      const merged = { ...prev, ...next };
      saveTweaks(merged);
      return merged;
    });
  }

  return [tweaks, update];
}

/**
 * 단독 다크 모드 토글 — TopBar(rightActions)에 배치. 라이트일 때 해(sun),
 * 다크일 때 달(moon) 아이콘. 클릭 시 즉시 전환 + localStorage 저장.
 */
export function TweaksPanel(): JSX.Element {
  const [tweaks, update] = useTweaks();
  const dark = tweaks.dark;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      aria-label="다크 모드"
      title={dark ? '라이트 모드로 전환' : '다크 모드로 전환'}
      data-testid="tweaks-dark-toggle"
      onClick={() => update({ dark: !dark })}
      className="kuun-topbar-ctl"
      style={triggerBtn}
    >
      {dark ? (
        // moon — 다크 모드 ON
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      ) : (
        // sun — 라이트 모드
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
        </svg>
      )}
    </button>
  );
}

const triggerBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
  padding: 0,
};
